import React, { RefObject, useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useXarrow } from 'react-xarrows';
import { ExtensionEventAction } from '@rossum/api-client/hooks';
import { IconLayoutGrid, IconSettings } from '@rossum/ui/icons/tabler';
import {
  GridLegacy,
  IconButton,
  Link,
  Stack,
  SvgIcon,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { getIcon } from '../../../../lib/helpers';
import { extensionDetailPath } from '../../../../routes';
import {
  ExtendedHook,
  ExtensionsGraphData,
  ExtensionsRelationMap,
} from '../helpers';
import { Line } from './Line';
import { DRAWER_WIDTH } from './UpdateDependenciesDrawer';

type Props = {
  graphData: ExtensionsGraphData;
  extensionGraphNodesMap: ExtensionsRelationMap;
  selectedExtension?: ExtendedHook | null;
  eventNames?: ExtensionEventAction[];
  onUpdateDependenciesClick?: (
    node: ExtendedHook | null,
    nodeEvents: ExtensionEventAction[] | null,
    nodeElement: RefObject<HTMLDivElement> | undefined
  ) => void;
  gridSx?: SxProps<Theme>;
};

export const DAG = ({
  graphData,
  extensionGraphNodesMap,
  selectedExtension,
  eventNames,
  gridSx,
  onUpdateDependenciesClick,
}: Props) => {
  const [actives, setActives] = useState<Array<ExtendedHook['url']>>([]);

  const intl = useIntl();
  const theme = useTheme();

  const updateXarrow = useXarrow();

  const refCollection = useRef<{
    [key: ExtendedHook['url']]: RefObject<HTMLDivElement>;
  }>({});

  const isNotDisabled =
    onUpdateDependenciesClick !== undefined && eventNames !== undefined;

  // return existing ref or assign new one
  const addRefToRefCollection = (key: ExtendedHook['url']) => {
    if (refCollection.current[key]) {
      return refCollection.current[key];
    }
    const newRef = React.createRef<HTMLDivElement>();
    refCollection.current[key] = newRef;
    return newRef;
  };

  useEffect(() => {
    updateXarrow();

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack
      justifyContent="flex-start"
      width={selectedExtension ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%'}
      sx={{ transition: 'all ease-in-out 0.2s' }}
    >
      <Stack
        sx={{
          position: 'relative',
          py: 4,
          overflowX: 'auto',
        }}
        onClick={() => setActives([])}
      >
        <GridLegacy
          container
          columnSpacing={8}
          wrap="nowrap"
          columns={graphData.length}
          sx={gridSx}
        >
          {graphData.map((column: Array<ExtendedHook>) => {
            return (
              <GridLegacy
                item
                key={column[0]?.url ?? 'empty-column-key'}
                sx={{
                  '&:nth-of-type(even)': { mt: 4.5 },
                }}
              >
                <Stack spacing={4}>
                  {column.map((node: ExtendedHook) => {
                    const currentNodeUrl = node.url;
                    const isDrawerOpen =
                      selectedExtension?.url === currentNodeUrl;

                    return (
                      <Stack key={currentNodeUrl}>
                        {node.runBefore.map(childUrl => {
                          const start = refCollection.current[currentNodeUrl];

                          const end = refCollection.current[childUrl];

                          const isActive =
                            actives.includes(childUrl) &&
                            actives.includes(currentNodeUrl);

                          return (
                            <Line
                              start={start}
                              end={end}
                              isActive={isActive}
                              key={`line-${childUrl}`}
                              dataCy={`from-${start?.current?.innerText
                                .toLowerCase()
                                .replaceAll(
                                  ' ',
                                  '-'
                                )}-to-${end?.current?.innerText
                                .toLowerCase()
                                .replaceAll(' ', '-')}`}
                            />
                          );
                        })}

                        <Stack
                          display="inline-flex"
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: isDrawerOpen
                              ? theme.palette.primary.main
                              : actives.includes(currentNodeUrl)
                                ? theme.palette.primary.main
                                : theme.palette.text.disabled,

                            backgroundColor: theme.palette.background.paper,
                            zIndex: actives.includes(currentNodeUrl) ? 3 : 1,
                            borderRadius: borderRadius('xl'),
                            minWidth: 200,
                            maxWidth: 300,
                            minHeight: 40,
                            pl: 2,
                            pr: 1,
                            '&:hover': {
                              borderColor: actives.includes(currentNodeUrl)
                                ? theme.palette.primary.light
                                : theme.palette.text.secondary,
                            },
                          }}
                          ref={addRefToRefCollection(currentNodeUrl)}
                          onClick={e => {
                            e.stopPropagation();

                            setActives([
                              currentNodeUrl,
                              ...(extensionGraphNodesMap[currentNodeUrl]
                                ?.mergedRelations ?? []),
                            ]);
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{
                              svg: {
                                display: 'block',
                              },
                              overflow: 'hidden',
                              opacity:
                                actives.length &&
                                !actives.includes(currentNodeUrl)
                                  ? 0.4
                                  : 1,
                            }}
                            data-cy={`extension-cell-${node.name
                              .toLowerCase()
                              .replaceAll(' ', '-')}`}
                          >
                            {getIcon(node.type, get(node.config, 'runtime'), {
                              color: node.active ? 'success' : 'secondary',
                            })}
                            <Tooltip title={node.name} enterNextDelay={500}>
                              <Typography
                                noWrap
                                sx={{ textOverflow: 'ellipsis' }}
                              >
                                {node.name}
                              </Typography>
                            </Tooltip>
                          </Stack>

                          <Stack direction="row">
                            <Tooltip
                              enterNextDelay={500}
                              title={intl.formatMessage({
                                id: 'containers.queueExtensions.contextMenu.extensionSettings',
                              })}
                            >
                              <Link
                                color="secondary"
                                component={RouterLink}
                                to={extensionDetailPath(node.id)}
                                data-cy="go-to-extension-settings"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: theme.palette.text.secondary,
                                  textDecoration: 'underline',
                                  '&:hover': {
                                    color: theme.palette.text.secondary,
                                    textDecoration: 'none',
                                  },
                                }}
                              >
                                <SvgIcon fontSize="medium">
                                  <IconSettings />
                                </SvgIcon>
                              </Link>
                            </Tooltip>

                            <Tooltip
                              enterNextDelay={500}
                              title={intl.formatMessage({
                                id: 'containers.queueExtensions.contextMenu.updateDependencies',
                              })}
                            >
                              <IconButton
                                onClick={e => {
                                  e.stopPropagation();
                                  const nodeElement = node.url
                                    ? refCollection.current[node.url]
                                    : undefined;

                                  if (isNotDisabled) {
                                    onUpdateDependenciesClick(
                                      node,
                                      eventNames,
                                      nodeElement
                                    );
                                  }
                                }}
                                size="small"
                                data-cy={`add-extension-dependencies-${node.name
                                  .toLowerCase()
                                  .replaceAll(' ', '-')}`}
                              >
                                <SvgIcon fontSize="medium">
                                  <IconLayoutGrid />
                                </SvgIcon>
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </GridLegacy>
            );
          })}
        </GridLegacy>
      </Stack>
    </Stack>
  );
};
